import { logger } from '@/utils/logging'
import type { AdBreak, AdBreakPosition } from './types'

/**
 * Fetches and parses a VMAP (Video Multiple Ad Playlist) XML document.
 * Extracts all ad breaks with their positions and VAST references.
 *
 * @returns Array of ad breaks sorted by position: pre-roll first, mid-rolls by time, post-roll last.
 */
export async function fetchAndParseVmap(vmapUrl: string, signal?: AbortSignal): Promise<AdBreak[]> {
  const response = await fetch(vmapUrl, { signal })
  if (!response.ok) {
    logger().warn('VMAP fetch failed', { status: response.status, url: vmapUrl })
    return []
  }

  const xml = await response.text()
  return parseVmapXml(xml)
}

function parseVmapXml(xml: string): AdBreak[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml, 'text/xml')

  const parseError = doc.querySelector('parsererror')
  if (parseError) {
    logger().warn('VMAP XML parse error', { error: parseError.textContent })
    return []
  }

  const adBreakElements = doc.querySelectorAll('vmap\\:AdBreak, AdBreak')
  const adBreaks: AdBreak[] = []

  for (const element of adBreakElements) {
    const position = parseTimeOffset(element.getAttribute('timeOffset'))
    if (position === null) continue

    const adSource = element.querySelector('vmap\\:AdSource, AdSource')
    if (!adSource) continue

    const adBreak = parseAdSource(adSource, position)
    if (adBreak) {
      adBreaks.push(adBreak)
    }
  }

  return adBreaks.sort(compareAdBreakPosition)
}

function parseAdSource(adSource: Element, position: AdBreakPosition): AdBreak | null {
  // Check for inline VAST first
  const vastInline = adSource.querySelector('vmap\\:VASTAdData, VASTAdData')
  if (vastInline) {
    const serializer = new XMLSerializer()
    const vastElement = vastInline.querySelector('VAST')
    if (vastElement) {
      return { position, vastInline: serializer.serializeToString(vastElement) }
    }
  }

  // Fall back to ad tag URI
  const adTagUri = adSource.querySelector('vmap\\:AdTagURI, AdTagURI')
  const vastUri = adTagUri?.textContent?.trim()
  if (vastUri) {
    return { position, vastUri }
  }

  return null
}

/**
 * Parses a VMAP timeOffset attribute into an AdBreakPosition.
 *
 * Supported formats:
 * - "start" → 'pre'
 * - "end" → 'post'
 * - "HH:MM:SS" or "HH:MM:SS.mmm" → seconds as a number
 * - "#N" (position marker) → ignored (returns null)
 * - Percentage ("N%") → ignored (requires content duration, not supported)
 */
function parseTimeOffset(offset: string | null): AdBreakPosition | null {
  if (!offset) return null

  if (offset === 'start') return 'pre'
  if (offset === 'end') return 'post'

  // HH:MM:SS or HH:MM:SS.mmm
  const timeMatch = offset.match(/^(\d+):(\d+):(\d+)(?:\.(\d+))?$/)
  if (timeMatch) {
    const hoursStr = timeMatch[1]
    const minutesStr = timeMatch[2]
    const secondsStr = timeMatch[3]
    if (!hoursStr || !minutesStr || !secondsStr) return null

    const hours = parseInt(hoursStr, 10)
    const minutes = parseInt(minutesStr, 10)
    const seconds = parseInt(secondsStr, 10)
    const millis = timeMatch[4] ? parseInt(timeMatch[4], 10) / Math.pow(10, timeMatch[4].length) : 0
    return hours * 3600 + minutes * 60 + seconds + millis
  }

  return null
}

/** Sort order: pre-roll < mid-rolls (by time) < post-roll. */
function compareAdBreakPosition(a: AdBreak, b: AdBreak): number {
  return toSortKey(a.position) - toSortKey(b.position)
}

function toSortKey(position: AdBreakPosition): number {
  if (position === 'pre') return -1
  if (position === 'post') return Infinity
  return position
}
